// @owners { team: patients-team }
import { type CartItem } from '@alto/orders_api/types/v1/cart_item';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCartData } from './useCartData';
import { getCartDetails } from '~shared/features/cart/selectors/getCartDetails';
import { getPrescriptionsHashWithUpdatedAt } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { type ItemPriceTotal } from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { type OrderPricing } from '~shared/features/pricing/types';
import { useOrderPricing } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types/clients';

type UsePrescriptionsInCart = {
  prescriptions: Prescription[];
  prescriptionsHash: Record<number, Prescription>;
  prescriptionIDs: number[];
  isPrescriptionInCart: (prescriptionId: number | null | undefined) => boolean;
  compounds: Prescription[];
  injectables: CartItem[];
  injectableCompounds: CartItem[];
  cartPricingTotals: Record<number, ItemPriceTotal>;
  hasInjectables: boolean;
  hasOnlyInjectableCompounds: boolean;
  hasCompounds: boolean;
  updatedAt: number;
};

export const usePrescriptionsInCartInitialState: UsePrescriptionsInCart = {
  prescriptions: [],
  prescriptionsHash: {},
  prescriptionIDs: [],
  isPrescriptionInCart: () => false,
  compounds: [],
  hasCompounds: false,
  hasInjectables: false,
  injectables: [],
  injectableCompounds: [],
  hasOnlyInjectableCompounds: false,
  updatedAt: 0,
  cartPricingTotals: {},
};

const DEFAULT_ORDER_PRICING: OrderPricing = {
  line_items: [],
  item_pricing: [],
  metadata: {
    estimated: false,
    expensive: false,
  },
};

export const usePrescriptionsInCart = () => {
  const { cartItems, updatedAt: cartUpdatedAt } = useCartData();
  const prescriptionsHash = useSelectorShared(getPrescriptionsHashWithUpdatedAt);
  const { orderPricing } = useOrderPricing();
  const lastUpdatedAtRef = useRef(Date.now());

  useEffect(() => {
    lastUpdatedAtRef.current = Date.now();
  }, [orderPricing]);

  const orderPricingWithUpdatedAt = useMemo(() => {
    return { ...(orderPricing ?? DEFAULT_ORDER_PRICING), updatedAt: lastUpdatedAtRef.current };
  }, [orderPricing]);

  const {
    cartPrescriptionIds,
    cartPrescriptions,
    cartCompounds,
    cartPricingTotals,
    cartPrescriptionIdHash,
    cartInjectable,
    cartInjectableCompounds,
  } = getCartDetails({
    cartItems,
    prescriptionsHash,
    orderPricing: orderPricingWithUpdatedAt,
    cartUpdatedAt,
  });
  const hasInjectables = cartInjectable.length > 0;
  const hasOnlyInjectableCompounds = hasInjectables && cartInjectable.length === cartInjectableCompounds.length;
  const hasCompounds = cartCompounds.length > 0;

  const isPrescriptionInCart = useCallback(
    (prescriptionId: number | null | undefined): boolean =>
      !!prescriptionId && !!cartPrescriptionIdHash[prescriptionId],
    [cartPrescriptionIdHash],
  );

  return {
    prescriptions: cartPrescriptions,
    prescriptionsHash: cartPrescriptionIdHash,
    prescriptionIDs: cartPrescriptionIds,
    isPrescriptionInCart,
    compounds: cartCompounds,
    injectables: cartInjectable,
    injectableCompounds: cartInjectableCompounds,
    cartPricingTotals,
    hasInjectables,
    hasOnlyInjectableCompounds,
    hasCompounds,
    updatedAt: cartUpdatedAt,
  };
};
